import {toast} from "react-toastify";
import {useDeleteDriverCustomItemNoteMutation, useLoadSingleDriverCustomItemNoteLazyQuery, useUpsertDriverCustomItemNoteMutation} from "../../../__generated__/generated_types";
import {useEffect, useState} from "react";
import {DriverNote} from "../DriverNoteModal";
import {UseDriverNoteModal} from "./useWsDriverNoteModal";


export interface CustomDriverNoteModalProps {
    onSave?: () => void;
}

export default function useCustomDriverNoteModal(props?: CustomDriverNoteModalProps): UseDriverNoteModal {
    const [driverNoteData, setDriverNoteData] = useState<DriverNote | null>(null);

    const [fetchNoteLocal, {data: noteData, loading: noteLoading}] = useLoadSingleDriverCustomItemNoteLazyQuery({fetchPolicy: "no-cache"});

    const [deleteNote, {loading: noteDeleting}] = useDeleteDriverCustomItemNoteMutation({
        notifyOnNetworkStatusChange: true,
        onCompleted(data) {
            if (data.deleteDriverCustomItemNote) {
                toast.success("Note deleted");
                props?.onSave?.();
            }
        },
    });

    const [upsertNote, {loading: noteSaving}] = useUpsertDriverCustomItemNoteMutation({
        notifyOnNetworkStatusChange: true,
        onCompleted(data) {
            if (data.upsertDriverCustomItemNote) {
                toast.success("Note updated");
                props?.onSave?.();
            }
        },
    });

    useEffect(() => {
        const note = noteData?.loadSingleDriverCustomItemNote;
        if (note) {
            setDriverNoteData(prev => {
                if (!prev) {
                    return null;
                }
                return {
                    lineType: "custom",
                    lineId: prev.lineId,
                    lineName: note.lineName,
                    lineValuesLocked: note.lineValuesLocked,
                    note: note.note,
                    isReadOnly: note.isReadOnly,
                };
            });
        }
    }, [noteData]);

    // user experience should be the same whether they are creating or updating a note, so a
    // partial driverNote object is still valid to launch the modal. If the note exists, then as the fetch
    // gets data back it will populate the rest of the object.
    function fetchNote(lineId: string): void {
        setDriverNoteData({
            lineType: "custom",
            lineId,
            lineName: "",
            lineValuesLocked: false,
            note: null,
            isReadOnly: null,
        });

        fetchNoteLocal({
            variables: {
                driverCustomItemId: lineId,
            },
        });
    }

    // I figured if the note theyre trying to save is empty, we could just go ahead and delete
    // the note record out of the DB, but if that seems like too heavy of an assumption we can make it an explicit interaction
    function save(driverNote: DriverNote): void {
        const trimmed = driverNote.note?.trim() ?? "";

        if (trimmed === "") {
            deleteNote({
                variables: {
                    driverCustomItemId: driverNote.lineId,
                },
            });
        } else {
            upsertNote({
                variables: {
                    note: {
                        lineId: driverNote.lineId,
                        note: driverNote.note ?? "",
                        isReadOnly: driverNote.isReadOnly ?? false,
                    },
                },
            });
        }
    }

    function reset() {
        setDriverNoteData(null);
    }

    return {
        driverNoteData: driverNoteData,
        isBusy: noteLoading || noteSaving || noteDeleting,
        fetchNote: fetchNote,
        save: save,
        reset: reset,
    };
}
